import { alpha } from "@mui/material";
import { MAX_CONTENT_WIDTH } from "helpers/breakpoints";
import { COLOR_BLACK, COLOR_GREY_BORDER, COLOR_WHITE } from "helpers/colors";
import { FONT_FAMILY_GRAPHIK } from "helpers/FONT";
import { SPACING, SPACING_HALF } from "helpers/spacings";
import { AccountCircleOutline, AccountGroup, CalendarMonth, Magnify, MenuDown, TrophyAward } from "mdi-material-ui";
import React, { useEffect, useRef, useState } from "react";

const baseFieldStyles = {
  borderRadius: 12,
  border: `1px solid ${COLOR_GREY_BORDER}`,
  padding: "0.8em 1em",
  fontFamily: FONT_FAMILY_GRAPHIK,
  fontSize: 14,
  backgroundColor: COLOR_WHITE,
  color: COLOR_BLACK,
  width: "100%",
  outline: "none",
  appearance: "none",
  boxShadow: `0 1px 4px ${alpha(COLOR_BLACK, 0.2)}`,
};

function FieldShell({ icon: IconComponent, showArrow = false, arrowRotated = false, children }) {
  const iconColor = alpha(COLOR_BLACK, 0.6);
  return (
    <div
      style={{
        position: "relative",
        width: "100%",
      }}
    >
      {IconComponent && (
        <IconComponent
          style={{
            position: "absolute",
            left: "1rem",
            top: "50%",
            transform: "translateY(-50%)",
            fontSize: 20,
            color: iconColor,
            pointerEvents: "none",
          }}
        />
      )}
      {children}
      {showArrow && (
        <MenuDown
          style={{
            position: "absolute",
            right: "0.8rem",
            top: "50%",
            transform: `translateY(-50%) rotate(${arrowRotated ? 180 : 0}deg)`,
            transition: "transform 0.2s ease",
            color: COLOR_BLACK,
            pointerEvents: "none",
          }}
        />
      )}
    </div>
  );
}

function TextInputField({ icon, style, ...props }) {
  return (
    <FieldShell icon={icon}>
      <input
        {...props}
        style={{
          ...style,
          paddingLeft: "2.8em",
        }}
      />
    </FieldShell>
  );
}

function SelectField({ icon, style, children, onFocus, onBlur, ...props }) {
  const [focused, setFocused] = useState(false);
  return (
    <FieldShell icon={icon} showArrow arrowRotated={focused}>
      <select
        {...props}
        style={{
          ...style,
          paddingLeft: "2.8em",
          paddingRight: "2.8em",
        }}
        onFocus={(event) => {
          setFocused(true);
          onFocus?.(event);
        }}
        onBlur={(event) => {
          setFocused(false);
          onBlur?.(event);
        }}
      >
        {children}
      </select>
    </FieldShell>
  );
}

const getIsCompact = () => {
  if (typeof window === "undefined") return false;
  return window.innerWidth < MAX_CONTENT_WIDTH;
};

export default function GamesFilterBar({
  keyword,
  onKeywordChange,
  years = [],
  selectedYear,
  onYearChange,
  leagues = [],
  selectedLeagueId,
  onLeagueChange,
  teams = [],
  selectedTeamId,
  onTeamChange,
  profiles = [],
  selectedProfileId,
  onProfileChange,
  onReset,
}) {
  const inputProps = {
    style: baseFieldStyles,
  };

  const [isCompact, setIsCompact] = useState(getIsCompact);
  const [filtersOpen, setFiltersOpen] = useState(() => !getIsCompact());
  const compactRef = useRef(isCompact);

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }
    const handleResize = () => {
      setIsCompact(window.innerWidth < MAX_CONTENT_WIDTH);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!isCompact) {
      setFiltersOpen(true);
    } else if (compactRef.current === false && isCompact) {
      setFiltersOpen(false);
    }
    compactRef.current = isCompact;
  }, [isCompact]);

  const handleKeywordChange = (event) => {
    onKeywordChange?.(event.target.value);
  };

  const handleSelectChange = (handler) => (event) => {
    if (!handler) return;
    const value = event.target.value;
    handler(value || null);
  };

  const showFilters = !isCompact || filtersOpen;
  const gridTemplateColumns = isCompact
    ? "1fr"
    : [
        "minmax(200px, 1fr)",
        "minmax(250px, 1fr)",
        "minmax(250px, 1fr)",
        "minmax(250px, 1fr)",
        "minmax(50px, 1fr)",
      ].join(" ");

  const truncate = (str, maxLength = 60) => {
    if (!str) return "";
    return str.length > maxLength ? str.slice(0, maxLength) + "..." : str;
  };

  const filterControls = (
    <div
      style={{
        display: "grid",
        gridTemplateColumns,
        gap: SPACING_HALF,
        marginTop: SPACING_HALF,
        maxWidth: MAX_CONTENT_WIDTH,
      }}
    >
      <SelectField
        icon={CalendarMonth}
        value={selectedYear || ""}
        onChange={handleSelectChange(onYearChange)}
        style={inputProps.style}
        name="year-filter"
      >
        <option value="">All years</option>
        {years.map((year) => (
          <option key={year} value={year}>
            {year}
          </option>
        ))}
      </SelectField>
      <SelectField
        icon={TrophyAward}
        value={selectedLeagueId || ""}
        onChange={handleSelectChange(onLeagueChange)}
        style={inputProps.style}
        name="league-filter"
      >
        <option value="">All leagues</option>
        {leagues.map((league) => (
          <option key={league.id} value={league.id}>
            {truncate(league.name)}
          </option>
        ))}
      </SelectField>
      <SelectField
        icon={AccountGroup}
        value={selectedTeamId || ""}
        onChange={handleSelectChange(onTeamChange)}
        style={inputProps.style}
        name="team-filter"
      >
        <option value="">All teams</option>
        {teams.map((team) => (
          <option key={team.id} value={team.id}>
            {truncate(team.name)}
          </option>
        ))}
      </SelectField>
      <SelectField
        icon={AccountCircleOutline}
        value={selectedProfileId || ""}
        onChange={handleSelectChange(onProfileChange)}
        style={inputProps.style}
        name="profile-filter"
      >
        <option value="">All profiles</option>
        {profiles.map((profile) => (
          <option key={profile.id} value={profile.id}>
            {truncate(profile.name + (profile.org ? ` - ${profile.org.name}` : ""))}
          </option>
        ))}
      </SelectField>
      <button
        type="button"
        onClick={() => onReset?.()}
        style={{
          border: "none",
          borderRadius: 999,
          padding: "0.65em 1.5em",
          fontFamily: FONT_FAMILY_GRAPHIK,
          fontWeight: 600,
          backgroundColor: COLOR_BLACK,
          color: COLOR_WHITE,
          cursor: "pointer",
          alignSelf: isCompact ? "stretch" : "center",
          justifySelf: isCompact ? "stretch" : "end",
        }}
      >
        Clear
      </button>
    </div>
  );

  return (
    <div
      style={{
        padding: `${SPACING} 0`,
        display: "flex",
        flexDirection: "column",
        gap: 0,
        width: `calc(100vw - ${SPACING} * 2)`,
        maxWidth: MAX_CONTENT_WIDTH,
        margin: `0 auto`,
      }}
    >
      <div
        style={{
          display: "flex",
          flexWrap: isCompact ? "nowrap" : "wrap",
          alignItems: "center",
          gap: SPACING_HALF,
          justifyContent: "center",
        }}
      >
        <TextInputField
          type="search"
          value={keyword ?? ""}
          onChange={handleKeywordChange}
          placeholder="Search teams or leagues"
          icon={Magnify}
          style={inputProps.style}
        />
        {isCompact && (
          <button
            type="button"
            onClick={() => setFiltersOpen((value) => !value)}
            style={{
              border: `1px solid ${alpha(COLOR_BLACK, 0.2)}`,
              borderRadius: 999,
              padding: "0.55em 1.5em",
              fontFamily: FONT_FAMILY_GRAPHIK,
              backgroundColor: COLOR_WHITE,
              cursor: "pointer",
              flex: "0 0 auto",
            }}
          >
            {filtersOpen ? "Close" : "Filters"}
          </button>
        )}
      </div>
      {showFilters && filterControls}
    </div>
  );
}
